import { ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Loader from 'components/Loader/Loader'
import { UserProvider, getInitialAccountState } from 'hooks/user'
import React, { Suspense, createContext, useContext, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'

import { NotificationType, Notifications } from './components/Notifications/Notifications'
import { useNotifications } from './hooks/useNotifications'
import './i18n'
import reportWebVitals from './reportWebVitals'
import { router } from './router/router'
import { theme } from './utils/theme'

import './index.css'

const queryClient = new QueryClient()

interface NotificationContextType {
  addNotification: (notification: Omit<NotificationType, 'id'>) => void
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function useNotificationContext() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider')
  }
  return context
}

function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { notifications, addNotification, removeNotification } = useNotifications()

  const value = useMemo(() => ({ addNotification }), [addNotification])

  return (
    <>
      <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
      <Notifications notifications={notifications} onClose={removeNotification} />
    </>
  )
}

const init = async () => {
  const initialAccountState = await getInitialAccountState()
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

  root.render(
    <React.StrictMode>
      <Suspense fallback={<Loader />}>
        <QueryClientProvider client={queryClient}>
          <UserProvider initialState={initialAccountState}>
            <ThemeProvider theme={theme}>
              <NotificationProvider>
                <RouterProvider router={router} />
              </NotificationProvider>
            </ThemeProvider>
          </UserProvider>
        </QueryClientProvider>
      </Suspense>
    </React.StrictMode>
  )
}
/* eslint-disable-next-line no-console */
init().then(() => console.info('App is init. 🔥'))
reportWebVitals()
